import { useState, useCallback, useEffect, useRef } from 'react'

// Keyboard navigation for search dropdowns / typeaheads — the shared behavior
// every filtered-list picker in the app should get: ArrowDown/ArrowUp move a
// highlight, Enter picks it, Escape closes, Home/End jump. Mouse still works;
// hovering a row should call setActiveIndex so keyboard and pointer share one
// highlight.
//
// Deliberately headless: it owns only the active index and the key handler, not
// any markup, because the pickers it serves render wildly different rows
// (buttons, divs, avatars). Callers spread `onKeyDown` on their input, put
// `ref={listRef}` on the scroll container, and tag each option with
// `data-ti-index={i}` so the hook can scroll the active one into view.
//
// Usage:
//   const { activeIndex, setActiveIndex, onKeyDown, listRef } = useTypeahead({
//     count: results.length,
//     onSelect: (i) => choose(results[i]),
//     onClose: () => setOpen(false),
//   })
//   <input onKeyDown={onKeyDown} ... />
//   <div ref={listRef}>
//     {results.map((r, i) => (
//       <button data-ti-index={i}
//         onMouseEnter={() => setActiveIndex(i)}
//         className={i === activeIndex ? 'active…' : '…'} … />
//     ))}
//   </div>
export function useTypeahead({ count, onSelect, onClose, enabled = true, loop = true, autoHighlight = true }) {
  const [activeIndex, setActiveIndex] = useState(-1)
  const listRef = useRef(null)

  // Whenever the result set changes size, land the highlight on the first row
  // (or clear it when empty) so Enter picks the top hit without an arrow press,
  // and a stale index can never point past the end of a shrunk list.
  //
  // `autoHighlight: false` starts with NO row highlighted (index -1) and keeps it
  // there until the user arrows down. That is what free-text pickers want: Enter
  // should commit the typed value, not silently snap to an existing option the
  // user never chose. Arrowing in still highlights and picks normally.
  useEffect(() => {
    setActiveIndex(count > 0 && autoHighlight ? 0 : -1)
  }, [count, autoHighlight])

  // Keep the highlighted row visible when arrowing past the visible fold.
  useEffect(() => {
    if (activeIndex < 0 || !listRef.current) return
    const el = listRef.current.querySelector(`[data-ti-index="${activeIndex}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])

  const onKeyDown = useCallback((e) => {
    if (!enabled) return
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        if (count === 0) return
        setActiveIndex((i) => (i + 1 >= count ? (loop ? 0 : count - 1) : i + 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        if (count === 0) return
        setActiveIndex((i) => (i <= 0 ? (loop ? count - 1 : 0) : i - 1))
        break
      case 'Home':
        if (count === 0) return
        e.preventDefault()
        setActiveIndex(0)
        break
      case 'End':
        if (count === 0) return
        e.preventDefault()
        setActiveIndex(count - 1)
        break
      case 'Enter':
        if (activeIndex >= 0 && activeIndex < count) {
          e.preventDefault()
          onSelect?.(activeIndex)
        }
        break
      case 'Escape':
        onClose?.()
        break
      default:
        break
    }
  }, [enabled, count, loop, activeIndex, onSelect, onClose])

  return { activeIndex, setActiveIndex, onKeyDown, listRef }
}
